import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToasts } from 'common/toast';
import { AppConfig } from '../../../../Config';

import { Dropdown, DropdownProps, Message, Pagination, Icon, SemanticICONS, SemanticCOLORS, Button, Form, Input } from 'semantic-ui-react';
import { getCurrentOrganization, isCurrentMemberOrgAdmin, isCurrentUserStaffOrSuperAdmin, isCurrentUserSuperUser, } from '../../../../common/selectors/auth';

import { isEmployeeStatusActive } from '../utils/EmployeeUtils';

import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow, } from 'common/components/Table';
import { FieldLabelTop, H2, H3, PageContent, PageHeader, PageSection } from 'common/styles/page';
import MemberCreateModal from './MemberCreateModal';
import ImportMemberModal from './ImportMemberModal';
import { StyledAddButton, StyledButton } from 'common/components/button/StyledButton';
import styled from 'styled-components';
import ImportMemberRecordModal from './ImportMemberRecordModal';

import { authenticatedFetch } from 'common/utils/authFetch';
import { employeeApi } from '../api/employeeApi';
import { ApiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieveApiResponse } from 'store/generated/portal/employee/employees';
import { useApiPortalV1EmployeeEmailResendInvitationCreateMutation } from 'store/generated/portal/employee/email';
import { useGetEmployeeTeamsQuery, useGetAllTeamsQuery, useGetOrganizationMembersQuery } from '../../../team-management/api/teamApi';
import LoadingSpinner from 'common/components/LoadingSpinner';
import userContextSlice from '../../../../common/slices/userContextSlice';
import EmailDeliveryModal from 'common/components/EmailDeliveryModal';
import PositionedModal from 'common/components/PositionedModal';
import { END_OF_SERVICE_REASONS, EndOfServiceReason } from '../utils/endOfServiceReasons';


interface MemberManagementProps {
  selectedMemberId?: number;
  onDeleteMember?: (memberId: number) => void;
  selectedAccountId?: number;
  onAccountSelect?: (accountId: number) => void;
}

const PAGE_SIZE = 9;
const STATUS_FILTER_VALUES = new Set(['active', 'inactive']);
const ACTIVE_FILTER_BORDER_COLOR = '#5aa9ff';

interface PersistedMemberFilters {
  statusFilter?: string;
  typeFilter?: number;
  teamFilter?: number | null;
}

const getFiltersStorageKey = (organizationId?: number) =>
  `member-management-filters:${organizationId ?? 'global'}`;

const getFilterSelectStyle = (isActive: boolean, minWidth: string) => ({
  height: '36px',
  border: `${isActive ? '2px' : '1px'} solid ${isActive ? ACTIVE_FILTER_BORDER_COLOR : '#ccc'}`,
  borderRadius: '5px',
  fontSize: '14px',
  minWidth,
  boxShadow: isActive ? '0 0 0 1px rgba(90, 169, 255, 0.28)' : 'none',
  background: isActive ? 'rgba(90, 169, 255, 0.03)' : '#fff',
});

const MemberManagement: React.FC<MemberManagementProps> = ({
  selectedMemberId,
  selectedAccountId,
}) => {
  const navigate = useNavigate();
  const { addToast } = useToasts();

  const [currentPage, setCurrentPage] = useState(1);

  const organization = useSelector(getCurrentOrganization);
  const isAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isSuperUser = useSelector(isCurrentUserSuperUser);
  const isOrgAdmin = useSelector(isCurrentMemberOrgAdmin);

  const canManageMembers = useMemo(() => isAdmin || isOrgAdmin, [isAdmin, isOrgAdmin]);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<
    ApiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieveApiResponse | undefined
  >(undefined);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isRecordImportModalOpen, setIsRecordImportModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'insert' | 'update'>('insert');
  const [archiveModalMember, setArchiveModalMember] = useState<ApiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieveApiResponse | null>(null);
  const [archiveDepartureDate, setArchiveDepartureDate] = useState<string>('');
  const [archiveDepartureReason, setArchiveDepartureReason] = useState<EndOfServiceReason | ''>('');
  const [deleteModalMember, setDeleteModalMember] = useState<ApiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieveApiResponse | null>(null);

  const [error, setError] = useState<string | undefined>(undefined);
  const [isMemberLoading, setIsMemberLoading] = useState(false);
  const [teamFilter, setTeamFilter] = useState<number | null>(null);
  const { data: teamsData } = useGetAllTeamsQuery();
  const demographicFields = useSelector(userContextSlice.selectors.getDemographicFields);

  const primaryCategoryLabel = useMemo(() => {
    if (!demographicFields) return 'Team';

    // demographicFields is a record keyed by field name. Look for a field with priority === 1
    const entries = Object.entries(demographicFields);
    const primary = entries.find(([_key, field]: any) => field && field.priority === 1);
    if (primary) return (primary[1].displayName as string) || (primary[1].name as string) || 'Team';

    // Fallback to first defined demographic field's displayName or name
    if (entries.length > 0) {
      const firstField: any = entries[0][1];
      return firstField.displayName || firstField.name || 'Team';
    }

    return 'Team';
  }, [demographicFields]);

  const isArchivedView = statusFilter === 'inactive';

  const departureReasonLabelMap = useMemo(
    () => END_OF_SERVICE_REASONS.reduce<Record<string, string>>((acc, reason) => {
      acc[reason.value] = reason.label;
      return acc;
    }, {}),
    []
  );

  const handleTeamFilterChange = useCallback((event: React.SyntheticEvent, data: DropdownProps) => {
    const value = typeof data.value === 'string' ? parseInt(data.value, 10) : Number(data.value);
    setTeamFilter(value === 0 ? null : value);
    setCurrentPage(1);
  }, []);

  const teamOptions = useMemo(() => {
    const options = [
      { key: '0', text: 'All Teams', value: 0 },
      { key: '-1', text: '[Not Assigned]', value: -1 },
    ];
    if (teamsData?.results) {
      // Sort teams alphabetically by name before building options
      const sortedTeams = [...teamsData.results].sort((a, b) => a.name.localeCompare(b.name));
      sortedTeams.forEach(team => {
        options.push({
          key: team.id.toString(),
          text: team.name,
          value: team.id,
        });
      });
    }
    return options;
  }, [teamsData]);

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'last_name',
    direction: 'asc',
  });

  const [createMember] = employeeApi.useApiPortalV1EmployeeEmployeesCreateMutation();
  const [updateMember] = employeeApi.useApiPortalV1EmployeeEmployeesPartialUpdateMutation();
  const [toggleStatus] = employeeApi.useApiPortalV1EmployeeEmployeesToggleStatusCreateMutation();
  const [deleteMember] = employeeApi.useDeleteMemberMutation();
  const [resendInvitation] = useApiPortalV1EmployeeEmailResendInvitationCreateMutation();

  const [typeFilter, setTypeFilter] = useState<number>(0);
  const [nameFilter, setNameFilter] = useState('');

  // Restore persisted select filters when organization changes.
  useEffect(() => {
    if (!organization?.id) return;

    try {
      const raw = sessionStorage.getItem(getFiltersStorageKey(organization.id));
      if (!raw) return;

      const parsed = JSON.parse(raw) as PersistedMemberFilters;

      if (parsed.statusFilter && STATUS_FILTER_VALUES.has(parsed.statusFilter)) {
        setStatusFilter(parsed.statusFilter);
      }

      if (typeof parsed.typeFilter === 'number' && !Number.isNaN(parsed.typeFilter)) {
        setTypeFilter(parsed.typeFilter);
      }

      if (
        parsed.teamFilter === null ||
        (typeof parsed.teamFilter === 'number' && !Number.isNaN(parsed.teamFilter))
      ) {
        setTeamFilter(parsed.teamFilter);
      }
    } catch (error) {
      // Ignore malformed persisted values and continue with defaults.
      console.error('Failed to restore member filters from sessionStorage', error);
    }
  }, [organization?.id]);

  // Persist the three select filters only (status, type, team).
  useEffect(() => {
    if (!organization?.id) return;

    try {
      const toPersist: PersistedMemberFilters = {
        statusFilter,
        typeFilter,
        teamFilter,
      };
      sessionStorage.setItem(getFiltersStorageKey(organization.id), JSON.stringify(toPersist));
    } catch (error) {
      console.error('Failed to persist member filters to sessionStorage', error);
    }
  }, [organization?.id, statusFilter, typeFilter, teamFilter]);

  // Email Log modal state
  const [emailLogOpen, setEmailLogOpen] = useState(false);
  const [emailLogAddress, setEmailLogAddress] = useState<string | null>(null);

  const hasEmployeeRecords = useMemo(
    () => organization?.employeeRecordsEnabled || false,
    [organization]
  );

  const handleTypeFilterChange = useCallback((event: React.SyntheticEvent, data: DropdownProps) => {
    const value = typeof data.value === 'string' ? parseInt(data.value, 10) : Number(data.value);
    setTypeFilter(!isNaN(value) ? value : 0);
    setCurrentPage(1);
  }, []);

  const handleAddMember = () => {
    setSelectedMember(undefined);
    setIsModalOpen(true);
  };

  const { data: membersData, isLoading, error: membersError } =
    employeeApi.useApiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieveQuery(
      { id: 0, accountId: selectedAccountId, organizationId: organization?.id || 0 },
      {
        skip: !organization?.id,
        refetchOnMountOrArgChange: true,
        refetchOnFocus: false,
      }
    );

  const { data: rosterReviewData } = employeeApi.useGetRosterReviewQuery(
    { organizationId: organization?.id || 0 },
    {
      skip: !organization?.id,
      refetchOnMountOrArgChange: true,
    }
  );

  const isRosterFinalized = Boolean(
    rosterReviewData?.cycle?.completed_at
    && !rosterReviewData?.cycle?.completion_invalidated_at
  );
  const rosterActionLabel = isRosterFinalized ? 'Update Roster' : 'Review Roster';
  const rosterActionColor = isRosterFinalized ? 'gray' : 'blue';

  const { data: orgMembersResponse } = useGetOrganizationMembersQuery(undefined, {
    skip: !organization?.id || teamFilter !== -1,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
    refetchOnReconnect: true,
  });

  const orgAssignedMemberIds = useMemo(() => {
    if (!orgMembersResponse?.results) return new Set<number>();
    return new Set(
      orgMembersResponse.results
        .filter((member) => member.assignedToTeam)
        .map((member) => member.id)
    );
  }, [orgMembersResponse]);

  const teamQueryParams = teamFilter === null || teamFilter === -1
    ? undefined
    : { team_id: teamFilter, limit: 1000 };

  const { data: employeeTeamsData } = useGetEmployeeTeamsQuery(
    teamQueryParams,
    {
      skip: !organization?.id || teamFilter === null || teamFilter === -1,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: true,
    }
  );

  // Get set of employee IDs that belong to the filtered team or assigned-to-team membership list for unassigned filtering
  const teamMemberIds = useMemo(() => {
    if (teamFilter === null) return null;
    if (teamFilter === -1) return orgAssignedMemberIds;
    if (!employeeTeamsData?.results) return null;
    return new Set(employeeTeamsData.results.map(et => et.employee_id));
  }, [teamFilter, employeeTeamsData, orgAssignedMemberIds]);

  const filteredMembers = useMemo(() => {
    if (!membersData) return [];

    const lowerNameFilter = nameFilter.trim().toLowerCase();

    const filtered = membersData.filter(
      (member: { status: any; account_type: number; first_name: string; last_name: string; id: number }) => {
        const matchesStatus =
          statusFilter === 'active'
            ? isEmployeeStatusActive(member.status)
            : statusFilter === 'inactive'
              ? !isEmployeeStatusActive(member.status)
              : true;

        const matchesType = typeFilter === 0 ? true : member.account_type === typeFilter;

        const matchesName =
          !lowerNameFilter ||
          member.first_name?.toLowerCase().startsWith(lowerNameFilter) ||
          member.last_name?.toLowerCase().startsWith(lowerNameFilter);

        // Filter by team if teamFilter is set
        let matchesTeam = true;
        if (teamFilter === -1) {
          matchesTeam = teamMemberIds ? !teamMemberIds.has(member.id) : true;
        } else if (teamFilter) {
          matchesTeam = teamMemberIds?.has(member.id) ?? false;
        }

        return matchesStatus && matchesType && matchesName && matchesTeam;
      }
    );

    return [...filtered].sort((a, b) => {
      let aVal: any = a[sortConfig.key];
      let bVal: any = b[sortConfig.key];

      if (sortConfig.key === 'status') {
        aVal = getMemberStatusMeta(a).order;
        bVal = getMemberStatusMeta(b).order;
      } else {
        aVal = aVal ?? '';
        bVal = bVal ?? '';
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [membersData, statusFilter, typeFilter, nameFilter, sortConfig, teamFilter, teamMemberIds]);

  const handleSort = (key: string) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const currentPageMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredMembers.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredMembers, currentPage]);

  const totalPages = useMemo(
    () => Math.ceil(filteredMembers.length / PAGE_SIZE),
    [filteredMembers.length]
  );

  const handlePageChange = useCallback((event: React.MouseEvent, data: any) => {
    setCurrentPage(data.activePage);
  }, []);

  const handleStatusFilterChange = useCallback(
    (event: React.SyntheticEvent, data: DropdownProps) => {
      setStatusFilter(data.value as string);
      setCurrentPage(1);
    },
    []
  );

  const handleEditMember = useCallback(
    (member: ApiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieveApiResponse) => {
      navigate(`${member.id}`);
    },
    [navigate]
  );

  interface MemberStatusMeta {
    label: string;
    icon: SemanticICONS;
    color: SemanticCOLORS;
    order: number;
    missingFields?: string[];
  }

  function getMemberStatusMeta(
    member: ApiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieveApiResponse
  ): MemberStatusMeta {
    const isActive = isEmployeeStatusActive(member.status);
    const missing = member.missing_demographic_fields ?? [];

    if (!isActive) {
      return {
        label: 'Archived',
        icon: 'archive',
        color: 'red',
        order: 3,
      };
    }

    if (missing.length === 0) {
      return {
        label: 'Complete',
        icon: 'check circle',
        color: 'green',
        order: 1,
        missingFields: [],
      };
    }

    return {
      label: 'Data Missing',
      icon: 'exclamation triangle',
      color: 'orange',
      order: 2,
      missingFields: member.missing_demographic_fields,
    };
  }

  function getMemberIcon(member: any) {
    let roleIcon = 'user' as SemanticICONS;
    let roleColor = 'grey' as SemanticCOLORS;
    let roleTitle = 'Member';

    if (member.account_type === 4) {
      roleIcon = 'shield' as SemanticICONS; // Organization Admin
      roleColor = 'green' as SemanticCOLORS;
      roleTitle = 'Organization Admin';
    } else if (member.account_type === 3) {
      roleIcon = 'users' as SemanticICONS; // Team Leader
      roleColor = 'blue' as SemanticCOLORS;
      roleTitle = 'Team Leader';
    }
    return { roleIcon, roleColor, roleTitle };
  }

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedMember(undefined);
    setError(undefined);
  };

  const handleSaveMember = async (
    member: ApiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieveApiResponse
  ) => {

    setIsMemberLoading(true);
    setError(undefined);

    try {
      if (modalMode === 'insert') {
        const createEmployee: any = {
          organization_id: organization?.id || 0,
          first_name: member.first_name,
          last_name: member.last_name,
          email: member.email,
          account_type: member.account_type,
          team_external_id: (member as any).team_external_id,
          survey_status: member.survey_status || 1,
        };

        const createPayload = {
          id: 0,
          createEmployee,
        };

        const result = await createMember(createPayload).unwrap(); // ← IMPORTANT
        const createdId = (result as any)?.id;

        // If demographics or employee record data were provided in the member payload, PATCH them after creation
        const patchPayload: any = {};
        if ((member as any).demographic_data) patchPayload.demographic_data = (member as any).demographic_data;
        if ((member as any).employee_record_data) patchPayload.employee_record_data = (member as any).employee_record_data;

        if (createdId && Object.keys(patchPayload).length > 0) {
          await updateMember({ id: createdId, pk: createdId, patchedPatchEmployee: patchPayload }).unwrap();
        }
      } else {
        await updateMember({
          id: member.id,
          pk: member.id,
          patchedPatchEmployee: {
            first_name: member.first_name,
            last_name: member.last_name,
            account_type: member.account_type,
            status: member.status,
            survey_status: member.survey_status,
            team_external_id: (member as any).team_external_id,
            email: member.email,
          },
        }).unwrap();
      }

      addToast('Member saved successfully', { appearance: 'success' });
      setIsModalOpen(false); // close only on success
    } catch (err: any) {
      const msg =
        err?.data?.detail ||
        err?.data?.message ||
        err?.error ||
        'Failed to save member';
      setError(msg);
      console.error('Error saving member:', err);
      addToast(msg, { appearance: 'error' });
    } finally {
      setIsMemberLoading(false);
    }
  };

  const handleToggleStatus = async (
    memberId: number,
    payload?: { departure_date?: string | null; departure_reason?: EndOfServiceReason | null }
  ) => {
    try {
      await toggleStatus({
        id: memberId,
        pk: memberId,
        toggleEmployeeStatus: payload,
      }).unwrap();
      addToast('Member status updated successfully', { appearance: 'success' });
    } catch (error) {
      addToast('Failed to update member status', { appearance: 'error' });
    }
  };

  const openArchiveModal = (member: ApiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieveApiResponse) => {
    setArchiveModalMember(member);
    setArchiveDepartureDate('');
    setArchiveDepartureReason('');
  };

  const closeArchiveModal = () => {
    setArchiveModalMember(null);
    setArchiveDepartureDate('');
    setArchiveDepartureReason('');
  };

  const confirmArchiveMember = async () => {
    if (!archiveModalMember) return;

    await handleToggleStatus(archiveModalMember.id, {
      departure_date: archiveDepartureDate || null,
      departure_reason: archiveDepartureReason || null,
    });
    closeArchiveModal();
  };

  const handleResendInvitation = async (member: ApiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieveApiResponse) => {
    try {
      await resendInvitation({
        body: { employee_id: member.id },
      }).unwrap();
      addToast(`Invitation email sent to ${member.email}`, { appearance: 'success' });
    } catch (error: any) {
      const errorMsg = error?.data?.error || 'Failed to send invitation';
      addToast(errorMsg, { appearance: 'error' });
    }
  };

  const canPermanentlyDeleteMember = useCallback(
    (member: ApiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieveApiResponse) => {
      const eligible = Boolean((member as any).can_be_permanently_deleted);
      const hasPrivilegedLogin =
        [3, 4].includes(Number((member as any).account_type))
        && (member as any).account_status !== null
        && (member as any).account_status !== undefined;
      return isSuperUser && eligible && !hasPrivilegedLogin;
    },
    [isSuperUser]
  );

  const confirmDeleteMember = async () => {
    if (!deleteModalMember) return;
    try {
      await deleteMember({ id: deleteModalMember.id, pk: deleteModalMember.id }).unwrap();
      addToast('Member deleted successfully', { appearance: 'success' });
      setDeleteModalMember(null);
    } catch (error: any) {
      const msg = error?.data?.detail || error?.data?.error || 'Failed to delete member';
      addToast(msg, { appearance: 'error' });
    }
  };

  const renderDemographicValidation = useCallback((member: any) => {
    const statusMeta = getMemberStatusMeta(member);

    const missingFields = statusMeta.missingFields?.length
      ? `Data Missing: ${statusMeta.missingFields.join(', ')}`
      : statusMeta.label;

    return (
      <ValidationIcon>
        <Icon name={statusMeta.icon} color={statusMeta.color} title={missingFields} size="large" />
        <span title={missingFields}>{statusMeta.label}</span>
      </ValidationIcon>
    );
  }, []);

  const getDepartureDateDisplay = useCallback((member: any) => {
    const rawDate = member?.departure_date;
    if (!rawDate) return '-';
    return String(rawDate).split('T')[0] || '-';
  }, []);

  const getDepartureReasonDisplay = useCallback(
    (member: any) => {
      const rawReason = member?.departure_reason;
      if (!rawReason) return '-';
      return departureReasonLabelMap[String(rawReason)] || String(rawReason);
    },
    [departureReasonLabelMap]
  );

  const handleDownloadRecordTemplate = async () => {
    if (!organization?.id) {
      addToast('Please select an organization', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    try {
      setIsDownloadingTemplate(true);

      // Construct the URL with query parameters
      const baseUrl = AppConfig.api.apiUrl;
      const endpoint = `/api/portal/v1/employee/import/record-template/`;
      const url = `${baseUrl}${endpoint}?organization_id=${organization?.id}`;

      const response = await authenticatedFetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-NULOGIC-ORGANIZATION-ID': organization?.id.toString(),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download template: ${response.status} ${response.statusText}`);
      }

      // Get the blob from the response
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      // Create and trigger download
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `record_import_template_${organization?.id}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      addToast('Template downloaded successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      console.error('Failed to download template:', error);
      addToast('Failed to download template', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleDownloadMembers = async () => {
    if (!organization?.id) {
      addToast('Please select an organization', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    try {
      setIsDownloadingTemplate(true);

      // Construct the URL with query parameters
      const baseUrl = AppConfig.api.apiUrl;
      const endpoint = `/api/portal/v1/employee/import/export/`;
      const url = `${baseUrl}${endpoint}?organization_id=${organization?.id}`;

      const response = await authenticatedFetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-NULOGIC-ORGANIZATION-ID': organization?.id.toString(),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download template: ${response.status} ${response.statusText}`);
      }

      // Get the blob from the response
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      // Create and trigger download
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `member_export_${organization?.id}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      addToast('Export downloaded successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      console.error('Failed to download member export:', error);
      addToast('Failed to download member export', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;


  if (membersError) {
    return (
      <Message negative>
        <Message.Header>Error loading organization members</Message.Header>
        <p>Please try again later.</p>
      </Message>
    );
  }



  return (
    <PageContent>
      <PageSection>
        <PageHeader
          title="Member Management"
          divider
          controls={
            canManageMembers && (
              <>
                <StyledAddButton
                  label="Add Member"
                  width="130px"
                  height="34px"
                  onClick={handleAddMember}
                />
                <StyledButton
                  label={rosterActionLabel}
                  color={rosterActionColor}
                  width="120px"
                  height="34px"
                  onClick={() => navigate('/app/team-management/roster-review')}
                />
                <Dropdown
                  button
                  icon="setting"
                  floating
                  className="icon"
                  style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                  direction="left"
                >
                  <Dropdown.Menu>
                    {isAdmin && (
                      <>
                        <Dropdown.Item
                          icon="download"
                          text="Member List Export"
                          onClick={e => {
                            handleDownloadMembers();
                          }}
                        />
                        <Dropdown.Item
                          icon="cloud upload"
                          text="Member Import/Update"
                          onClick={e => {
                            setModalMode('insert');
                            setIsImportModalOpen(true);
                          }}
                        />
                        <Dropdown.Divider />
                        {hasEmployeeRecords && (
                          <Dropdown.Item
                            icon="download"
                            text="Record Export"
                            onClick={e => {
                              handleDownloadRecordTemplate();
                            }}
                          />
                        )}
                        <Dropdown.Item
                          icon="cloud upload"
                          text="Record Import/Update"
                          onClick={e => {
                            setModalMode('insert');
                            setIsRecordImportModalOpen(true);
                          }}
                        />
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </>
            )
          }
        />

        <PageSection
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          style={{ marginBottom: '20px' }}
        >
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              placeholder="Filter by name"
              value={nameFilter}
              onChange={e => {
                setNameFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                height: '38px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                padding: '0 10px',
                fontSize: '14px',
                minWidth: '300px',
              }}
            />
              <Dropdown
                placeholder="Select a team"
                selection
                value={teamFilter || 0}
                onChange={handleTeamFilterChange}
                options={teamOptions}
                style={getFilterSelectStyle(teamFilter !== null, '200px')}
              />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <Dropdown
              placeholder="Filter by status"
              selection
              compact
              value={statusFilter}
              onChange={handleStatusFilterChange}
              options={[
                { key: 'active', text: 'Active', value: 'active' },
                { key: 'inactive', text: 'Archived', value: 'inactive' },
              ]}
              style={getFilterSelectStyle(statusFilter !== 'active', '120px')}
            />

            <Dropdown
              placeholder="Filter by type"
              selection
              value={typeFilter}
              onChange={handleTypeFilterChange}
              options={[
                { key: '0', text: 'All Members', value: 0 },
                { key: '1', text: 'Org Admins', value: 4 },
                { key: '2', text: 'Team Leaders', value: 3 },
                { key: '3', text: 'Regular Members', value: 9 },
              ]}
              style={getFilterSelectStyle(typeFilter !== 0, '180px')}
            />
          </div>
        </PageSection>

        {/* Members Table */}
        <Table>
          <TableHeader>
            <TableRow>
              <HeaderCell selectable onClick={() => handleSort('last_name')} width="4">
                Name{' '}
                {sortConfig.key === 'last_name' && (
                  <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                )}
              </HeaderCell>
              <HeaderCell selectable onClick={() => handleSort('email')} width="4">
                Email{' '}
                {sortConfig.key === 'email' && (
                  <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                )}
              </HeaderCell>
              <HeaderCell
                selectable
                onClick={() => handleSort(isArchivedView ? 'departure_date' : 'primary_category')}
                width="3"
              >
                {isArchivedView ? (
                  <>
                    Departure Date{' '}
                    {sortConfig.key === 'departure_date' && (
                      <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                    )}
                  </>
                ) : (
                  <>
                    {primaryCategoryLabel}{' '}
                    {sortConfig.key === 'primary_category' && (
                      <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                    )}
                  </>
                )}
              </HeaderCell>
              {isArchivedView && (
                <HeaderCell selectable onClick={() => handleSort('departure_reason')} width="3">
                  Departure Reason{' '}
                  {sortConfig.key === 'departure_reason' && (
                    <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                  )}
                </HeaderCell>
              )}
              <HeaderCell selectable onClick={() => handleSort('status')} width="3">
                Status{' '}
                {sortConfig.key === 'status' && (
                  <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                )}
              </HeaderCell>
              <HeaderCell width="1" justify="center">
                Actions
              </HeaderCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            {currentPageMembers.map(
              (member: ApiPortalV1EmployeeEmployeesOrganizationEmployeesRetrieveApiResponse) => {
                let { roleIcon, roleColor, roleTitle } = getMemberIcon(member);

                return (
                  <TableRow
                    $selectable
                    key={member.id}
                    active={selectedMemberId === member.id}
                    onClick={() => handleEditMember(member)}
                  >
                    <TableCell $firstcol>
                      <Icon name={roleIcon} color={roleColor} title={roleTitle} /> {member.last_name},{' '}
                      {member.first_name}
                    </TableCell>
                    <TableCell>{member.email}</TableCell>
                    {isArchivedView ? (
                      <>
                        <TableCell>{getDepartureDateDisplay(member)}</TableCell>
                        <TableCell>{getDepartureReasonDisplay(member)}</TableCell>
                      </>
                    ) : (
                      <TableCell>{member.primary_category || '-'}</TableCell>
                    )}
                    <TableCell>{renderDemographicValidation(member)}</TableCell>

                    <TableCell width={1} justify="center">
                      <Dropdown
                        button
                        icon="ellipsis vertical"
                        floating
                        className="icon"
                        direction="left"
                        style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                        onClick={e => e.stopPropagation()}
                      >
                        <Dropdown.Menu>
                          <>
                            <Dropdown.Item
                              text="Edit Member"
                              icon="edit"
                              onClick={e => {
                                e.stopPropagation();
                                handleEditMember(member);
                              }}
                            />
                            {/* NU-103: Only show Resend Invitation for INVITED accounts (status=1) */}
                            {(member.account_type === 3 || member.account_type === 4) && member.account_status === 1 && (
                              <Dropdown.Item
                                icon="redo"
                                text="Resend Invitation"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleResendInvitation(member);
                                }}
                              />
                            )}

                            {/* Email Log - opens modal showing recent emails for this member */}
                            <Dropdown.Item
                              text="Email Log"
                              icon="mail"
                              onClick={e => {
                                e.stopPropagation();
                                if (member.email) {
                                  setEmailLogAddress(member.email);
                                  setEmailLogOpen(true);
                                } else {
                                  addToast('No email available for this member', { appearance: 'error' });
                                }
                              }}
                            />
                            <Dropdown.Item
                              text={member.status === 1 ? 'Archive Member' : 'Unarchive Member'}
                              icon={member.status === 1 ? 'archive' : 'undo'}
                              onClick={e => {
                                e.stopPropagation();
                                if (member.status === 1) {
                                  openArchiveModal(member);
                                } else {
                                  handleToggleStatus(member.id);
                                }
                              }}
                            />
                            {canPermanentlyDeleteMember(member) && (
                              <Dropdown.Item
                                text="Delete Member"
                                icon="trash"
                                onClick={e => {
                                  e.stopPropagation();
                                  setDeleteModalMember(member);
                                }}
                              />
                            )}
                          </>
                        </Dropdown.Menu>
                      </Dropdown>
                    </TableCell>
                  </TableRow>
                );
              }
            )}
          </TableBody>
        </Table>

        <MemberCreateModal
          open={isModalOpen}
          onClose={handleModalClose}
          onSave={handleSaveMember}
          loading={isMemberLoading}
          error={error}
          member={selectedMember}
        />

        <PageSection display="flex" justifyContent="end" style={{ marginTop: '20px' }}>
          {totalPages > 1 && (
            <Pagination
              activePage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              firstItem={null}
              lastItem={null}
              prevItem={{ content: '‹', icon: true }}
              nextItem={{ content: '›', icon: true }}
            />
          )}
        </PageSection>

        <ImportMemberModal
          mode={modalMode}
          open={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
        />

        <ImportMemberRecordModal
          open={isRecordImportModalOpen}
          onClose={() => setIsRecordImportModalOpen(false)}
        />

        <PositionedModal open={Boolean(archiveModalMember)} onClose={closeArchiveModal} size="small">
          <div style={{ padding: 18 }}>
            <H2 padbottom="20px">Archive {archiveModalMember?.first_name} {archiveModalMember?.last_name}?</H2>

            <Form  style={{ padding: 8 }}>
              <Form.Field>
                <FieldLabelTop>Departure Date (optional)</FieldLabelTop>
                <Input
                  type="date"
                  value={archiveDepartureDate}
                  onChange={e => setArchiveDepartureDate(e.target.value)}
                />
              </Form.Field>
              <Form.Field>
                <FieldLabelTop>Departure Reason (optional)</FieldLabelTop>
                <Dropdown
                  placeholder="Select a reason"
                  fluid
                  selection
                  clearable
                  value={archiveDepartureReason || undefined}
                  options={END_OF_SERVICE_REASONS.map(reason => ({
                    key: reason.value,
                    text: reason.label,
                    value: reason.value,
                  }))}
                  onChange={(_, data) => {
                    setArchiveDepartureReason((data.value as EndOfServiceReason | undefined) || '');
                  }}
                />
              </Form.Field>
            </Form>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <Button type="button" onClick={closeArchiveModal}>
                Cancel
              </Button>
              <Button negative type="button" onClick={confirmArchiveMember}>
                Archive
              </Button>
            </div>
          </div>
        </PositionedModal>

        {/* Email Delivery modal used by the per-member actions */}
        <EmailDeliveryModal email={emailLogAddress} isOpen={emailLogOpen} onClose={() => setEmailLogOpen(false)} />

        <PositionedModal open={Boolean(deleteModalMember)} onClose={() => setDeleteModalMember(null)} size="small">
          <div style={{ padding: 18 }}>
            <H2 padbottom="20px">Delete {deleteModalMember?.first_name} {deleteModalMember?.last_name}?</H2>
            <p>
              This will permanently remove this member and related non-survey records. This action cannot be undone.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <Button type="button" onClick={() => setDeleteModalMember(null)}>
                Cancel
              </Button>
              <Button negative type="button" onClick={confirmDeleteMember}>
                Delete
              </Button>
            </div>
          </div>
        </PositionedModal>
      </PageSection>
    </PageContent>
  );
};

export default MemberManagement;

const ValidationIcon = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem; /* Adjust spacing between icon and text */

  .ui.icon {
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  span {
    display: inline-flex;
    align-items: center;
  }
`;
